import { useEffect, useRef, useState, useCallback } from 'react';
import { parseQuestionsRealtime, parseQuestionsRealtimeStream, ParsedQuestionFromAPI, SurveyMode } from '../utils/simulationApi';
import { ParsedQuestion } from '../components/survey/types';
import { mergeWithCached, normalizeText } from '../utils/questionMerge';
export { mergeWithCached } from '../utils/questionMerge';
import { TYPE_CODE_TO_FULL } from './typeCodeMap';

const DEBOUNCE_MS = 1200;

export interface UseRealtimeParsingResult {
  parsedQuestions: ParsedQuestion[];
  /** True during the very first parse when no cached results exist yet. */
  isParsing: boolean;
  /** True during a re-parse while stale (but valid) results are still visible. */
  isUpdating: boolean;
  /**
   * Latest error message from the realtime parse call (e.g. "raw_text
   * exceeds 15000 character limit for realtime parsing"). `null` when the
   * most recent attempt either succeeded, was aborted, was a no-op, or has
   * not run yet. Consumers should surface this to the user — silent
   * swallowing strands the respondent.
   */
  error: string | null;
  /** Feed user edits (type changes, images) back into the cache so they survive re-parses. */
  seedCache: (questions: ParsedQuestion[]) => void;
  /** Hard-reset the hook: clear cache + results (used by "Back to Questions"). */
  reset: () => void;
  /** Number of questions detected (for skeleton loading UI) */
  questionCount: number;
  /** Which question indices are still loading (for skeleton UI) */
  loadingIndices: Set<number>;
}

function mapApiQuestion(q: ParsedQuestionFromAPI, fallbackId: string): ParsedQuestion {
  return {
    id: q.id || fallbackId,
    text: q.text,
    type: q.type,
    choices: q.choices,
    scale_min: q.scale_min,
    scale_max: q.scale_max,
    max_selections: q.max_selections,
    ranking_count: q.ranking_count,
    text_limit: q.text_limit,
    image_paths: q.image_paths,
    image_descriptions: q.image_descriptions,
    image_attachment_level: q.image_attachment_level as 'question' | 'option' | undefined,
    option_images: q.option_images,
    maxdiff_items: q.maxdiff_items,
    conjoint_attributes: q.conjoint_attributes,
    conjoint_description: q.conjoint_description,
    matrix_rows: q.matrix_rows,
    matrix_columns: q.matrix_columns,
    is_followup: q.is_followup,
    followup_reason: q.followup_reason,
    section_id: q.section_id,
    section_title: q.section_title,
    section_context: q.section_context,
  };
}

/**
 * Dual-key cache: stores ParsedQuestion objects keyed by both question ID
 * and normalized text. ID is the primary lookup; text is a fallback when
 * the AI re-assigns IDs.
 */
class QuestionCache {
  private byId = new Map<string, ParsedQuestion>();
  private byText = new Map<string, ParsedQuestion>();

  set(q: ParsedQuestion) {
    this.byId.set(q.id, q);
    this.byText.set(normalizeText(q.text), q);
  }

  /** Look up by ID first, then fall back to normalized text. */
  get(id: string, text: string): ParsedQuestion | undefined {
    return this.byId.get(id) ?? this.byText.get(normalizeText(text));
  }

  /** Remove entries whose IDs are NOT in the given set (stale trim). */
  retainIds(ids: Set<string>) {
    for (const [key, q] of this.byId) {
      if (!ids.has(key)) {
        this.byId.delete(key);
        this.byText.delete(normalizeText(q.text));
      }
    }
  }

  clear() {
    this.byId.clear();
    this.byText.clear();
  }

  get size() { return this.byId.size; }
}

/**
 * Realtime question parsing hook with dual-key caching.
 *
 * - Debounces textarea input, sends text to backend for Haiku tagging
 * - Dual-key cache (ID + text) preserves user edits for unchanged questions
 * - Never clears visible results mid-parse; shows stale data with `isUpdating` flag
 * - Uses isParsingInternalRef guard to prevent seedCache from causing race conditions
 */
export default function useRealtimeQuestionTags(
  questionsText: string,
  goal: string,
  context: string,
  enabled: boolean = true,
  useTwoPassParsing: boolean = true,
  surveyMode: SurveyMode = 'ai_sim',
): UseRealtimeParsingResult {
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [loadingIndices, setLoadingIndices] = useState<Set<number>>(new Set());

  const abortRef = useRef<AbortController | null>(null);
  const streamAbortRef = useRef<{ abort: () => void } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastParsedTextRef = useRef<string>('');
  const lastResultRef = useRef<ParsedQuestion[]>([]);
  const cacheRef = useRef(new QuestionCache());
  // Guard to prevent seedCache from overwriting lastResultRef during active parsing
  const isParsingInternalRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    if (streamAbortRef.current) { streamAbortRef.current.abort(); streamAbortRef.current = null; }
    // Clear parsing state when cancelling to prevent stale state
    isParsingInternalRef.current = false;
  }, []);

  const seedCache = useCallback((questions: ParsedQuestion[]) => {
    const cache = cacheRef.current;
    for (const q of questions) {
      // Skip skeleton/loading questions to prevent cache corruption
      if ((q as any).isLoading) continue;
      cache.set(q);
    }
    // Only update lastResultRef if not actively parsing to prevent race conditions
    // The parent component calls seedCache via useEffect, which can overwrite
    // lastResultRef during an active parse and cause duplication bugs
    if (!isParsingInternalRef.current) {
      lastResultRef.current = questions;
    }
  }, []);

  const reset = useCallback(() => {
    cancel();
    setParsedQuestions([]);
    setIsParsing(false);
    setIsUpdating(false);
    setError(null);
    setQuestionCount(0);
    setLoadingIndices(new Set());
    lastParsedTextRef.current = '';
    lastResultRef.current = [];
    cacheRef.current.clear();
    isParsingInternalRef.current = false;
  }, [cancel]);

  useEffect(() => {
    console.log('[useRealtimeQuestionTags] useEffect triggered, enabled:', enabled, 'textLength:', questionsText.length, 'useTwoPassParsing:', useTwoPassParsing);
    if (!enabled) {
      console.log('[useRealtimeQuestionTags] Disabled, returning early');
      cancel();
      setIsUpdating(false);
      return;
    }

    const trimmed = questionsText.trim();

    if (!trimmed) {
      console.log('[useRealtimeQuestionTags] Empty text, clearing');
      cancel();
      setParsedQuestions([]);
      setIsParsing(false);
      setIsUpdating(false);
      setError(null);
      lastParsedTextRef.current = '';
      lastResultRef.current = [];
      cacheRef.current.clear();
      return;
    }

    if (trimmed === lastParsedTextRef.current) {
      console.log('[useRealtimeQuestionTags] Same text as before, skipping');
      return;
    }

    console.log('[useRealtimeQuestionTags] New text detected, starting debounce timer');
    cancel();

    const hasExisting = lastResultRef.current.length > 0;
    if (hasExisting) {
      setIsUpdating(true);
    }

    timerRef.current = setTimeout(async () => {
      console.log('[useRealtimeQuestionTags] Debounce timer fired, hasExisting:', hasExisting);
      // Mark parsing as active to prevent seedCache from overwriting lastResultRef
      isParsingInternalRef.current = true;
      if (!hasExisting) {
        setIsParsing(true);
      }

      const lastText = lastParsedTextRef.current;
      const cache = cacheRef.current;

      // Clear cache on full replacement
      if (lastText.length > 0) {
        const minLen = Math.min(lastText.length, trimmed.length);
        let prefixLen = 0;
        while (prefixLen < minLen && lastText[prefixLen] === trimmed[prefixLen]) {
          prefixLen++;
        }
        let suffixLen = 0;
        const maxSuffix = minLen - prefixLen;
        while (
          suffixLen < maxSuffix &&
          lastText[lastText.length - 1 - suffixLen] === trimmed[trimmed.length - 1 - suffixLen]
        ) {
          suffixLen++;
        }
        const sharedLen = prefixLen + suffixLen;
        if (sharedLen < lastText.length * 0.3) {
          cache.clear();
        }
      }

      // Use streaming for two-pass parsing
      console.log('[useRealtimeQuestionTags] useTwoPassParsing:', useTwoPassParsing);
      if (useTwoPassParsing) {
        console.log('[useRealtimeQuestionTags] Starting streaming parse...');
        // Streaming mode: show skeletons immediately after Pass 1
        const questionsMap = new Map<number, ParsedQuestion>();

        const stream = parseQuestionsRealtimeStream(trimmed, goal, context, {
          onSections: (sections) => {
            // Sectioned surveys emit `sections` instead of `boundaries`. Pre-size
            // the skeleton array (one per question across all sections) so out-of-
            // order onQuestion writes never leave undefined holes, and tag each
            // skeleton with its section so headers render while loading.
            const total = sections.reduce((n, s) => Math.max(n, s.start_index + s.num_questions), 0);
            if (total <= 0) return;
            setQuestionCount(total);
            setLoadingIndices(new Set(Array.from({ length: total }, (_, i) => i)));

            const skeletons: ParsedQuestion[] = Array.from({ length: total }, (_, i) => {
              const sec = sections.find(s => i >= s.start_index && i < s.start_index + s.num_questions);
              return {
                id: `Q${i + 1}`,
                text: `Loading question ${i + 1}...`,
                type: 'long_text',
                isLoading: true,
                section_id: sec?.id,
                section_title: sec?.title ?? undefined,
                section_context: sec?.context,
              } as ParsedQuestion;
            });
            setParsedQuestions(skeletons);
            setIsParsing(false);
            setIsUpdating(false);
          },
          onBoundaries: (boundaries) => {
            console.log('[useRealtimeQuestionTags] onBoundaries received:', boundaries.length);
            // Create skeleton questions from boundaries
            const count = boundaries.length;
            setQuestionCount(count);
            setLoadingIndices(new Set(boundaries.map((_, i) => i)));

            // Create initial skeleton questions with type from boundary
            const skeletons: ParsedQuestion[] = boundaries.map((b, i) => ({
              id: `Q${i + 1}`,
              text: `Loading question ${i + 1}...`,
              type: TYPE_CODE_TO_FULL[b.t] || 'long_text',
              isLoading: true,
            } as ParsedQuestion));

            setParsedQuestions(skeletons);
            setIsParsing(false); // Pass 1 done, show skeletons
            setIsUpdating(false); // Also clear updating so preview renders
          },
          onQuestion: (index, question) => {
            const mapped = mapApiQuestion(question, `Q${index + 1}`);
            const cached = cache.get(mapped.id, mapped.text);
            const final = cached ? mergeWithCached(mapped, cached) : mapped;

            questionsMap.set(index, final);
            cache.set(final);

            // Update loading indices
            setLoadingIndices(prev => {
              const next = new Set(prev);
              next.delete(index);
              return next;
            });

            // Update questions array
            setParsedQuestions(prev => {
              const updated = [...prev];
              updated[index] = final;
              return updated;
            });
          },
          onComplete: () => {
            setIsUpdating(false);
            setLoadingIndices(new Set());
            lastParsedTextRef.current = trimmed;
            lastResultRef.current = Array.from(questionsMap.entries())
              .sort(([a], [b]) => a - b)
              .map(([, q]) => q);
            // Mark parsing as complete
            isParsingInternalRef.current = false;
          },
          onError: (message) => {
            setError(message);
            setIsParsing(false);
            setIsUpdating(false);
            // Mark parsing as complete on error
            isParsingInternalRef.current = false;
          },
        }, surveyMode);

        streamAbortRef.current = stream;
      } else {
        // Non-streaming mode: use regular API
        // Always send the full text - the append-only optimization was removed
        // because it caused race conditions with the seedCache bidirectional sync
        const controller = new AbortController();
        abortRef.current = controller;

        try {
          const result = await parseQuestionsRealtime(
            trimmed, goal, context, controller.signal, surveyMode, false,
          );
          if (controller.signal.aborted) return;

          setError(null);

          const apiQuestions = result.questions.map(
            (q: ParsedQuestionFromAPI, i: number) => mapApiQuestion(q, `Q${i + 1}`),
          );

          // Always process full results - no append-only optimization
          const finalQuestions = apiQuestions.map((q, i) => {
            const id = q.id || `Q${i + 1}`;
            const cached = cache.get(id, q.text);
            if (cached) {
              return mergeWithCached({ ...q, id }, cached);
            }
            return { ...q, id };
          });

          const activeIds = new Set(finalQuestions.map(q => q.id));
          for (const q of finalQuestions) {
            cache.set(q);
          }
          cache.retainIds(activeIds);

          lastParsedTextRef.current = trimmed;
          lastResultRef.current = finalQuestions;
          setParsedQuestions(finalQuestions);
        } catch (err: unknown) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          if (err instanceof Error && err.name === 'AbortError') return;
          const message =
            err instanceof Error && err.message
              ? err.message
              : 'Realtime question parsing failed.';
          console.warn('Realtime question parsing failed:', err);
          setError(message);
        } finally {
          // Mark parsing as complete
          isParsingInternalRef.current = false;
          if (!controller.signal.aborted) {
            setIsParsing(false);
            setIsUpdating(false);
          }
        }
      }
    }, DEBOUNCE_MS);

    return () => cancel();
  }, [questionsText, goal, context, enabled, useTwoPassParsing, surveyMode, cancel]);

  useEffect(() => {
    return () => cancel();
  }, [cancel]);

  return { parsedQuestions, isParsing, isUpdating, error, seedCache, reset, questionCount, loadingIndices };
}
